import {
  useRef,
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Raycaster, Vector2, ArrowHelper, Object3D } from 'three';
import { radiansToDegrees } from '@/utils/function/util';
import usePanoramaStore from '@/store/panorama';

interface RaycastContextType {
  hoveredObject: Object3D | null;
}

const RaycastContext = createContext<RaycastContextType | undefined>(undefined);

interface RaycastProviderProps {
  children: ReactNode;
}

export function RaycastProvider({
  children,
}: RaycastProviderProps): JSX.Element {
  const raycaster = useRef(new Raycaster());
  const mouse = useRef(new Vector2());
  const { camera, gl, scene } = useThree();
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [hoveredObject, setHoveredObject] = useState<Object3D | null>(null);
  const rayHelper = useRef<ArrowHelper | null>(null);
  const [firstFrame, setFirstFrame] = useState(true);
  const setArrowLong = usePanoramaStore((state) => state.setArrowLong);
  useEffect(() => {
    if (!firstFrame) return;

    setIsSceneReady(true);
    setFirstFrame(false);

    rayHelper.current = new ArrowHelper(
      raycaster.current.ray.direction,
      raycaster.current.ray.origin,
      5,
      0xffffff
    );
    scene.add(rayHelper.current);

    return () => {
      // 컴포넌트 언마운트 시 정리
      if (rayHelper.current) {
        scene.remove(rayHelper.current);
        rayHelper.current = null;
      }
    };
  }, [firstFrame, raycaster, scene]);

  useFrame(() => {
    if (!isSceneReady) return;

    raycaster.current.setFromCamera(mouse.current, camera);
    const intersects = raycaster.current.intersectObjects(
      scene.children.filter(
        (obj) =>
          obj.name !== 'EquirectangularViewer' && obj !== rayHelper.current
      ),
      true
    );
    const rotation = camera.rotation;
    const adjustedYDegree = -(
      ((radiansToDegrees(rotation.y) + 270) % 360) -
      180
    );
    setArrowLong(adjustedYDegree);
    if (rayHelper.current) {
      rayHelper.current.position.copy(camera.position);
      rayHelper.current.setDirection(raycaster.current.ray.direction);
    }

    if (intersects.length > 0) {
      if (hoveredObject !== intersects[0].object) {
        setHoveredObject(intersects[0].object);
      }
    } else if (hoveredObject !== null) {
      setHoveredObject(null);
    }
  });

  const handleMouseMove = (event: MouseEvent) => {
    const { clientX, clientY } = event;
    const { left, top, width, height } = gl.domElement.getBoundingClientRect();
    mouse.current.x = ((clientX - left) / width) * 2 - 1;
    mouse.current.y = -((clientY - top) / height) * 2 + 1;
  };

  useEffect(() => {
    gl.domElement.addEventListener('mousemove', handleMouseMove);
    return () => {
      gl.domElement.removeEventListener('mousemove', handleMouseMove);
    };
  }, [gl]);

  return (
    <RaycastContext.Provider value={{ hoveredObject }}>
      {children}
    </RaycastContext.Provider>
  );
}

export function useRaycast(): RaycastContextType {
  const context = useContext(RaycastContext);
  if (context === undefined) {
    throw new Error('useRaycast must be used within a RaycastProvider');
  }
  return context;
}

export default function RaycastManager(): null {
  return null;
}
